import type { ResolvedPreferences } from '../contract/types';

/**
 * Translating resolved preferences into the parameters one endpoint expects.
 *
 * Endpoints that generate CSVs and reports server-side format the output themselves, so the user's
 * preferences have to travel with the request. What makes that awkward is that the backend has no
 * single vocabulary for them: the same concept arrives as `distanceUnit` on one endpoint and
 * `distancePreference` on another, and those two disagree about what omitting them means — one
 * defaults to miles, the other to kilometres. A dialect is the declaration of what a single
 * endpoint calls things, so that knowledge lives in one reviewable place instead of being
 * rediscovered at each call site.
 */

/**
 * A mapping from preferences to every localization parameter of `TArg`, named by `TKeys`.
 *
 * `TKeys` is narrower than `keyof TArg` because an endpoint argument carries far more than
 * preferences — organization ids, date ranges, request bodies. Only the preference-derived subset
 * is declared here.
 *
 * String keys only. A parameter is a name on the wire, and {@link toServerParams} assembles its
 * result with `Object.entries`, which never reports a symbol — so a symbol key would be a
 * parameter the type promises and the request could never carry.
 *
 * Two modifiers carry the guarantees:
 *
 * - `-?` strips the optionality the generated client puts on these fields, so a dialect that omits
 *   a parameter it declared fails to compile. Because the backend defaults contradict each other,
 *   leaving a parameter off is never a safe shorthand for "use the default".
 * - `NonNullable` applies the same rule to the value, so no mapper can return `undefined`.
 *
 * Declaring a dialect with `satisfies Dialect<SomeApiArg, ...>` anchors it to the generated type:
 * if the backend renames or narrows a parameter, the next client regeneration breaks the build
 * rather than the CSV.
 */
export type Dialect<TArg, TKeys extends Extract<keyof TArg, string>> = {
  readonly [K in TKeys]-?: (
    preferences: ResolvedPreferences
  ) => NonNullable<TArg[K]>;
};

/** What a dialect produces: the same keys, resolved to values, none of them optional. */
export type ServerParams<TArg, TKeys extends Extract<keyof TArg, string>> = {
  readonly [K in TKeys]-?: NonNullable<TArg[K]>;
};

type ParameterMapper = (preferences: ResolvedPreferences) => unknown;

/**
 * Applies a dialect, producing the parameters to spread into a request.
 *
 * Every declared parameter is always emitted. Nothing is checked for absence at runtime because
 * nothing can be absent: `ResolvedPreferences` has no optional fields, so a well-typed mapper
 * always has an answer to give.
 *
 * @param dialect - The endpoint's declared vocabulary. See {@link Dialect}.
 * @param preferences - The signed-in user's resolved preferences.
 * @returns One property per declared parameter. See {@link ServerParams}.
 */
export const toServerParams = <TArg, TKeys extends Extract<keyof TArg, string>>(
  dialect: Dialect<TArg, TKeys>,
  preferences: ResolvedPreferences
): ServerParams<TArg, TKeys> => {
  const mappers = Object.entries(dialect) as [string, ParameterMapper][];

  return Object.fromEntries(
    mappers.map(([parameter, mapper]) => [parameter, mapper(preferences)])
  ) as ServerParams<TArg, TKeys>;
};
